import React from "react";
import ReactDOM from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider as MuiThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

import { App } from "@/app";
import { AuthProvider } from "@/auth/auth-provider";
import { ToastProvider } from "@/components/ui/toast";
import { DialogDraftNavigationGuard } from "@/components/dialog-draft";
import "@/index.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// MUI dark theme that picks up our Tailwind tokens so DataGrid blends in.
// Brand palette: red primary, Anietronic dark blue secondary, black/grey/white chrome.
const muiTheme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: "#e11d2e" },
    secondary: { main: "#2c5aa8" },
    error: { main: "#dc2626" },
    background: { default: "#121212", paper: "#1a1a1a" },
    text: { primary: "#fafafa", secondary: "#a3a3a3" },
    divider: "#2d2d2f",
  },
  shape: { borderRadius: 10 },
  typography: { fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif' },
});

const router = createBrowserRouter([{
  path: "*",
  element: <DialogDraftNavigationGuard><App /></DialogDraftNavigationGuard>,
}]);

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline enableColorScheme />
        <ToastProvider>
          <AuthProvider>
            <RouterProvider router={router} />
          </AuthProvider>
        </ToastProvider>
      </MuiThemeProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
